/**
 * Print-size rules — per-CLIENT-org print dimensions (cm) by product type and
 * size, set by the 3PL OWNER (`org_print_size_rules.print_size_rules`).
 *
 * At print/resize time an explicit rule OVERRIDES whatever resize behaviour a
 * SKU would otherwise get (including `resize_mode='source'` — BLU-673/674):
 * the rule's `{ widthCm, heightCm }` converts to target pixels at 300 DPI
 * (`cmToPx`) and is fed as the `fit: 'inside'` target dims, so aspect ratio is
 * preserved and undersized artwork is scaled up exactly like the existing
 * platen resize. No rule match, or any error loading/resolving the map, means
 * exactly today's behaviour (fail-open, never blocks, never throws out of the
 * apply path) — see `resize-for-platen.ts` / `film-size-rules.ts`.
 *
 * Map shape: `productType -> sizeCode|NO_SIZE -> { widthCm, heightCm }`. Mirrors
 * the RIP routing map's key-safety posture (`./rip-routing`) — codes reuse
 * `RIP_CODE_REGEX`, and the NO_SIZE sentinel matches `design-shape-map.ts`'s
 * `NO_SIZE_KEY` precedent (product types/lines with no size dimension, or a
 * per-order custom whose composed stem has no size segment).
 */
import { RIP_CODE_REGEX } from './rip-routing.js';
/** Sentinel size key for a rule that applies regardless of (or absent) size. */
export const PRINT_SIZE_NO_SIZE_KEY = '__nosize__';
/** Inclusive cm bounds — matches the write-path zod + the DB CHECK. 120cm is a
 *  physical max for any film/platen the bridge/print-agent can produce. */
export const PRINT_SIZE_CM_MIN = 1;
export const PRINT_SIZE_CM_MAX = 120;
/**
 * OOM guard (matches `MAX_INPUT_PIXELS` in `resize.ts` / `resize-for-platen.ts`):
 * a resolved rule's target dims must never ask sharp to allocate more than this
 * many pixels. Enforced inside `resolvePrintSizeRule` (via `isValidRule`) so
 * every consumer inherits it. With `PRINT_SIZE_CM_MAX` at 120 a same-axis-
 * bounded rule can never actually reach this cap (120cm² @300dpi ≈ 200.9M px,
 * below the 268.4M threshold) — this guard is defense-in-depth against a
 * future `PRINT_SIZE_CM_MAX` raise or a stale/pre-migration stored value with
 * larger cm dims than the current bound allows.
 */
export const PRINT_SIZE_MAX_TARGET_PIXELS = 268_435_456;
/** Bound the doc so a malformed/hostile payload can't store an unbounded map
 *  — mirrors the write-path zod (`print-size-rules.ts` router); re-enforced
 *  here so a direct DB/PostgREST write bypassing that zod can't defeat it. */
export const PRINT_SIZE_MAX_PRODUCT_TYPES = 64;
export const PRINT_SIZE_MAX_LEAVES_PER_TYPE = 64;
// ---------------------------------------------------------------------------
// Helpers.
// ---------------------------------------------------------------------------
/**
 * Own-property read that tolerates malformed (org-supplied JSONB) values:
 * non-objects, arrays, null, and inherited/prototype keys all yield undefined.
 * (Mirrors `getOwn` in `rip-routing.ts` / `design-shape-map.ts`.)
 */
function getOwn(obj, key) {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
        return undefined;
    }
    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
        return undefined;
    }
    return obj[key];
}
/** Product-type sub-table lookup, with dual `2` prefix strip (`2HD` → `HD`) —
 *  mirrors `rip-routing.ts`'s `getTypeEntry`. */
function getTypeTable(map, productType) {
    let table = getOwn(map, productType);
    if (table === undefined && productType.startsWith('2')) {
        table = getOwn(map, productType.slice(1));
    }
    return table;
}
/** Runtime re-check of a looked-up leaf — defense-in-depth even though the
 *  loader/sanitizer already bound every stored value. Also enforces the
 *  target-pixel OOM guard (see `PRINT_SIZE_MAX_TARGET_PIXELS`). */
function isValidRule(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const { widthCm, heightCm } = value;
    if (!isValidCm(widthCm) || !isValidCm(heightCm))
        return false;
    return cmToPx(widthCm) * cmToPx(heightCm) <= PRINT_SIZE_MAX_TARGET_PIXELS;
}
function isValidCm(value) {
    return (typeof value === 'number' &&
        Number.isFinite(value) &&
        value >= PRINT_SIZE_CM_MIN &&
        value <= PRINT_SIZE_CM_MAX);
}
// ---------------------------------------------------------------------------
// Sanitizer.
// ---------------------------------------------------------------------------
/**
 * Sanitize an untrusted/raw print-size-rules doc into a safe SUBSET: keys
 * (productType / sizeCode) uppercased and checked against `RIP_CODE_REGEX`
 * (or equal to `PRINT_SIZE_NO_SIZE_KEY`); leaf values must be finite numbers
 * within `[PRINT_SIZE_CM_MIN, PRINT_SIZE_CM_MAX]`, rounded to 1 decimal.
 * Invalid leaves/types are dropped silently (mirrors `sanitizeRipRoutingMap`'s
 * skip-bad-rows posture) — a single bad leaf never blanks the whole map.
 * Also bounds the doc's SHAPE (`PRINT_SIZE_MAX_PRODUCT_TYPES` /
 * `PRINT_SIZE_MAX_LEAVES_PER_TYPE` — mirrors the write-path zod, re-enforced
 * here so a direct DB/PostgREST write bypassing that zod can't store an
 * unbounded map): excess entries beyond the cap are dropped, deterministically
 * in `Object.entries` iteration order (first N valid entries win, same as any
 * later entry would if the doc were re-saved through this sanitizer).
 * Returns a FRESH object (safe to mutate).
 */
export function sanitizePrintSizeRules(raw) {
    const out = {};
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return out;
    for (const [productType, entry] of Object.entries(raw)) {
        if (Object.keys(out).length >= PRINT_SIZE_MAX_PRODUCT_TYPES)
            break;
        const typeKey = productType.toUpperCase();
        if (!RIP_CODE_REGEX.test(typeKey))
            continue;
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry))
            continue;
        const cleanSizes = {};
        for (const [sizeCode, value] of Object.entries(entry)) {
            if (Object.keys(cleanSizes).length >= PRINT_SIZE_MAX_LEAVES_PER_TYPE)
                break;
            const sizeKey = sizeCode === PRINT_SIZE_NO_SIZE_KEY ? PRINT_SIZE_NO_SIZE_KEY : sizeCode.toUpperCase();
            if (sizeKey !== PRINT_SIZE_NO_SIZE_KEY && !RIP_CODE_REGEX.test(sizeKey))
                continue;
            if (typeof value !== 'object' || value === null || Array.isArray(value))
                continue;
            const widthCm = sanitizeCm(value.widthCm);
            const heightCm = sanitizeCm(value.heightCm);
            if (widthCm === null || heightCm === null)
                continue;
            cleanSizes[sizeKey] = { widthCm, heightCm };
        }
        if (Object.keys(cleanSizes).length === 0)
            continue;
        out[typeKey] = cleanSizes;
    }
    return out;
}
function sanitizeCm(value) {
    if (!isValidCm(value))
        return null;
    return Math.round(value * 10) / 10;
}
// ---------------------------------------------------------------------------
// Resolver.
// ---------------------------------------------------------------------------
/**
 * Resolve `(productType, sizeCode)` → the print-size rule.
 *
 * Lookup: `map[productType][sizeCode]`, with the dual `2` prefix stripped from
 * the product-type code when the raw code has no entry (`2HD` → `HD`, mirrors
 * `resolvePlaten`). An exact size match wins; otherwise the `NO_SIZE` entry is
 * tried; otherwise `null`. An empty/undefined `sizeCode` goes straight to the
 * `NO_SIZE` entry. Every returned value is re-checked against the cm bounds
 * AND the `PRINT_SIZE_MAX_TARGET_PIXELS` OOM guard at runtime (defense-in-
 * depth) — a candidate failing either check falls through (to `NO_SIZE`, then
 * to `null`). Never throws.
 */
export function resolvePrintSizeRule(map, productType, sizeCode) {
    if (typeof productType !== 'string' || productType.length === 0)
        return null;
    const table = getTypeTable(map, productType.toUpperCase());
    if (table === undefined)
        return null;
    const upperSize = typeof sizeCode === 'string' ? sizeCode.toUpperCase() : '';
    if (upperSize.length > 0) {
        const exact = getOwn(table, upperSize);
        if (isValidRule(exact))
            return exact;
    }
    const fallback = getOwn(table, PRINT_SIZE_NO_SIZE_KEY);
    return isValidRule(fallback) ? fallback : null;
}
/** 300 DPI px-per-cm conversion: `cm / 2.54in * 300dpi`, rounded. */
export function cmToPx(cm) {
    return Math.round((cm / 2.54) * 300);
}
